/**
 * Casca da aplicacao (spec 0009).
 *
 * Sem router: o contrato de URL vem da spec 0003 e e `?report=<id>` - deep-link e botao voltar do
 * navegador. Trocar por um router hoje quebraria links que ja circulam.
 *
 * O catalogo vive na casca do Auth Manager: a navegacao lista os workspaces que a pessoa alcanca, e
 * o escopo da barra superior e a empresa (o tenant). A leitura e TELA CHEIA, fora da casca - o
 * relatorio fica com a janela inteira (spec 0003, R4).
 */

import { useQuery } from '@tanstack/react-query'
import { useEffect, useLayoutEffect, useMemo, useState } from 'react'

import { listReports } from './api/reports'
import { AuthGate } from './auth/AuthGate'
import { fetchMe } from './auth/api'
import { CatalogPage } from './catalog/CatalogPage'
import { agrupar, TODOS } from './catalog/workspaces'
import { ReportView } from './report/ReportView'
import { AppShell, NavGroup, NavLink } from './shell/AppShell'
import { aplicarTema, lerTema } from './shell/theme'
import './styles/app.css'

function reportDaUrl(): string | null {
  return new URLSearchParams(window.location.search).get('report')
}

export function App() {
  const [reportId, setReportId] = useState<string | null>(() => reportDaUrl())
  const [workspace, setWorkspace] = useState<string>(TODOS)

  // Antes da primeira pintura: sem isto o login piscava no escuro antes de virar claro.
  useLayoutEffect(() => {
    aplicarTema(lerTema())
  }, [])

  // O botao voltar do navegador precisa sair do relatorio, nao da aplicacao (spec 0003).
  useEffect(() => {
    function aoNavegar() {
      setReportId(reportDaUrl())
    }
    window.addEventListener('popstate', aoNavegar)
    return () => window.removeEventListener('popstate', aoNavegar)
  }, [])

  function fechar() {
    window.history.pushState({}, '', window.location.pathname)
    setReportId(null)
  }

  return (
    <AuthGate>
      {reportId === null ? (
        <Catalogo
          onAbrir={(id) => {
            window.history.pushState({}, '', '?report=' + encodeURIComponent(id))
            setReportId(id)
          }}
          onWorkspace={setWorkspace}
          workspace={workspace}
        />
      ) : (
        <ReportView onClose={fechar} reportId={reportId} />
      )}
    </AuthGate>
  )
}

function Catalogo({
  workspace,
  onAbrir,
  onWorkspace,
}: {
  workspace: string
  onAbrir: (id: string) => void
  onWorkspace: (nome: string) => void
}) {
  const relatorios = useQuery({ queryKey: ['reports'], queryFn: listReports })
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe, retry: false })
  const grupos = useMemo(() => agrupar(relatorios.data ?? []), [relatorios.data])

  const nav = (
    <>
      <NavGroup titulo="Catálogo">
        <NavLink
          atual={workspace === TODOS}
          contagem={relatorios.data?.length}
          icon="list"
          label="Todos"
          onClick={() => onWorkspace(TODOS)}
        />
      </NavGroup>
      {grupos.length === 0 ? null : (
        <NavGroup titulo="Workspaces">
          {grupos.map(([nome, itens]) => (
            <NavLink
              atual={workspace === nome}
              contagem={itens.length}
              icon="layers"
              key={nome}
              label={nome}
              onClick={() => onWorkspace(nome)}
            />
          ))}
        </NavGroup>
      )}
    </>
  )

  // A empresa e o ESCOPO, como o tenant no Auth Manager: o catalogo inteiro e dela.
  const escopo =
    me.data === undefined ? undefined : (
      <div className="scope-control__campo">
        <span>Empresa</span>
        <strong>{me.data.tenant_name ?? me.data.tenant_id}</strong>
      </div>
    )

  return (
    <AppShell nav={nav} scope={escopo}>
      <CatalogPage onOpen={onAbrir} workspace={workspace} />
    </AppShell>
  )
}
